/**
 * The Predictive Energy Modeller's access gate.
 *
 * PRESENTATION ONLY. The token, its storage, its validation and its error
 * handling all come from `useTokenGate` — the same mechanism the fleet gate
 * uses. What differs is that someone arriving at the modeller should not be
 * asked to sign in to another product.
 *
 * It says ACCESS TOKEN, not sign-in. There is no account here and the copy
 * must not imply one: this is a shared staff credential, and dressing it as a
 * login would suggest an identity the system does not have.
 */

import { useTokenGate } from '../../api/useTokenGate';
import '../tokens.css';
import './gate.css';

export function ModellerGate({ onAuthenticated }: { onAuthenticated: () => void }) {
  const gate = useTokenGate(onAuthenticated);

  return (
    <div className="pem pem-gate">
      <div className="pem-gate__panel">
        <img className="pem-gate__mark" src="/equoia-feathers-600.png" alt="" width={56} height={56} />
        <div className="pem-eyebrow">Mobile battery power</div>
        <h1 className="pem-gate__title">Predictive Energy Modeller</h1>
        <p className="pem-gate__lead">
          An internal Equoia tool. It states a production's energy requirement
          in kilowatt-hours and kilowatts — and declines to answer where the
          evidence does not support one.
        </p>

        <form onSubmit={gate.submit} className="pem-gate__form">
          <label className="pem-label" htmlFor="pem-token">Access key</label>
          <input
            id="pem-token"
            type="password"
            value={gate.value}
            autoFocus
            placeholder="your access key"
            autoComplete="off"
            onChange={(e) => gate.setValue(e.target.value)}
          />

          {/*
            THE SECOND KEY IS OPTIONAL AND SAYS WHY.
            The server keeps two separate credentials and neither can do the
            other's job — one views, one changes. Asking for both here is the
            only way a person can create a production AND then look at it.
            Someone who only needs to look leaves this blank and the product
            simply offers them nothing to change.
          */}
          <label className="pem-label pem-gate__second" htmlFor="pem-write-token">
            Editing key <span>optional</span>
          </label>
          <input
            id="pem-write-token"
            type="password"
            value={gate.writeValue}
            placeholder="leave blank to view only"
            autoComplete="off"
            onChange={(e) => gate.setWriteValue(e.target.value)}
          />

          <div className="pem-gate__row">
            <button type="submit" disabled={gate.busy || !gate.value.trim()}>
              {gate.busy ? 'Checking' : 'Enter'}
            </button>
          </div>

          <p className="pem-gate__note">
            Keys are issued by Equoia and shared by the team — there is no
            personal account and nothing to sign up for. Add the editing key
            only if you need to create or change anything.
          </p>
          {gate.error && <p className="pem-gate__error">{gate.error}</p>}
        </form>
      </div>
    </div>
  );
}
